import React, { useRef, useState } from 'react';
import type { AnalysisResult } from '../types';
import useClickOutside from '../hooks/useClickOutside';

const ExportDropdown: React.FC<{ result: AnalysisResult }> = ({ result }) => {
  type JsPDFConstructor = new (...args: unknown[]) => {
    internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
    setFont: (...args: unknown[]) => void;
    setFontSize: (n: number) => void;
    splitTextToSize: (text: string, width: number) => string[];
    addPage: () => void;
    text: (lines: string[] | string, x: number, y: number) => void;
    save: (name: string) => void;
  };
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useClickOutside(ref, () => setOpen(false), open);

  const download = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const ensureJsPdf = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const anyWin = window as unknown as Record<string, unknown>;
      const jspdfObj = (anyWin.jspdf as unknown) as { jsPDF?: unknown } | undefined;
      if (jspdfObj && jspdfObj.jsPDF) return resolve();
      // Inject script tag from CDN
      const existing = document.querySelector('script[data-jspdf]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load jsPDF')));
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
      s.setAttribute('data-jspdf', '1');
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load jsPDF'));
      document.head.appendChild(s);
    });
  };

  const stripMarkdown = (md: string) => {
    if (!md) return '';
    let out = md;
    // Remove fenced code blocks
    out = out.replace(/```[\s\S]*?```/g, '');
    // Remove images ![alt](url)
    out = out.replace(/!\[(.*?)\]\((.*?)\)/g, '$1');
    // Convert links [text](url) -> text
    out = out.replace(/\[(.*?)\]\((.*?)\)/g, '$1');
    // Remove headings ####
    out = out.replace(/^#+\s?/gm, '');
    // Remove emphasis *, **, _, ~~
    out = out.replace(/(\*\*|\*|__|_|~~)/g, '');
    // Remove inline code backticks
    out = out.replace(/`([^`]*)`/g, '$1');
    // Remove unordered list markers
    out = out.replace(/^[\s]*[-*+]\s+/gm, '');
    // Remove ordered list markers
    out = out.replace(/^[\s]*\d+\.\s+/gm, '');
    // Trim excessive blank lines
    out = out.replace(/\n{3,}/g, '\n\n');
    return out.trim();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="px-3 py-1.5 bg-bg-secondary hover:bg-bg-tertiary border border-border-secondary rounded-md text-sm inline-flex items-center gap-2"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>Download</span>
        <svg className="w-3 h-3 text-text-tertiary" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path d="M5 7l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-bg-primary border border-border-secondary rounded-md shadow-2xl py-1 z-[9999]">
          <button
            onClick={() => download(result.analysis, `${result.categoryName || 'analysis'}.md`, 'text/markdown')}
            className="w-full text-left px-3 py-2 text-sm hover:bg-bg-secondary"
          >
            Markdown (.md)
          </button>
          <button
            onClick={() => download(result.analysis, `${result.categoryName || 'analysis'}.txt`, 'text/plain')}
            className="w-full text-left px-3 py-2 text-sm hover:bg-bg-secondary"
          >
            Text (.txt)
          </button>
          <button
            onClick={async () => {
              try {
                const plain = stripMarkdown(result.analysis || '');
                await ensureJsPdf();
                const anyWin = window as unknown as Record<string, unknown>;
                const jspdfObj = (anyWin.jspdf as unknown) as { jsPDF?: unknown } | undefined;
                const jsPDF = jspdfObj && jspdfObj.jsPDF ? (jspdfObj.jsPDF as unknown as JsPDFConstructor) : ((anyWin.jsPDF as unknown) as JsPDFConstructor | undefined);
                if (!jsPDF) {
                  download(plain, `${result.categoryName || 'analysis'}.pdf`, 'application/pdf');
                  return;
                }
                const DocConstructor = jsPDF as JsPDFConstructor;
                const doc = new DocConstructor({ unit: 'pt', format: 'letter' } as unknown as object);
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                const margin = 40; // pts
                const usableWidth = pageWidth - margin * 2;
                const fontSize = 11;
                doc.setFont('Times');
                doc.setFontSize(fontSize);

                const lines = doc.splitTextToSize(plain, usableWidth);
                const lineHeight = fontSize * 1.2;
                const headerHeight = 40;
                const footerHeight = 30;
                const usableHeight = pageHeight - margin * 2 - headerHeight - footerHeight;
                const linesPerPage = Math.floor(usableHeight / lineHeight);

                let page = 0;
                for (let i = 0; i < lines.length; i += linesPerPage) {
                  const chunk = lines.slice(i, i + linesPerPage);
                  if (page > 0) doc.addPage();
                  // Header
                  doc.setFontSize(12);
                  doc.setFont('Times', 'bold');
                  doc.text(result.categoryName || 'Analysis', margin, margin + 12);
                  doc.setFontSize(fontSize);
                  doc.setFont('Times', 'normal');

                  // Text body
                  const startY = margin + headerHeight;
                  doc.text(chunk, margin, startY);

                  // Footer with page number
                  const footerY = pageHeight - margin - 10;
                  doc.setFontSize(9);
                  doc.text(`Page ${page + 1}`, pageWidth - margin - 50, footerY);

                  page += 1;
                }
                doc.save(`${result.categoryName || 'analysis'}.pdf`);
              } catch (e) {
                const plain = stripMarkdown(result.analysis || '');
                download(plain, `${result.categoryName || 'analysis'}.txt`, 'text/plain');
              }
            }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-bg-secondary"
          >
            PDF (.pdf)
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportDropdown;
